import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon, Spinner, StatusIndicator } from '@amzn/awsui-components-core';
import { MeetingNotification } from '../../interfaces/interface';
import './NotificationBell.scss';

const TYPE_COLORS: Record<string, string> = {
  vote: '#0972d3',
  comment: '#037f0c',
  reply: '#5f6b7a'
};

const TYPE_STATUS: Record<string, 'success' | 'info' | 'stopped'> = {
  vote: 'info',
  comment: 'success',
  reply: 'stopped'
};

const TYPE_LABELS: Record<string, string> = {
  vote: 'Vote',
  comment: 'Comment',
  reply: 'Reply'
};

function timeAgo(dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateString).toLocaleDateString();
}

function formatMessage(n: MeetingNotification): string {
  switch (n.type) {
    case 'vote':
      return `${n.actorName} voted ${n.voteDirection === 'up' ? '👍' : '👎'}${n.slotLabel ? ` on ${n.slotLabel}` : ''}`;
    case 'comment':
      return `${n.actorName} commented${n.commentPreview ? `: "${n.commentPreview}"` : ''}`;
    case 'reply':
      return `${n.actorName} replied${n.commentPreview ? `: "${n.commentPreview}"` : ''}`;
    default:
      return `${n.actorName} performed an action`;
  }
}

function buildMeetingUrl(n: MeetingNotification): string {
  const creator = n.meetingCreator ?? '';
  return `${window.location.origin}/ea-huddle?meetingId=${encodeURIComponent(n.meetingId)}&creator=${encodeURIComponent(creator)}`;
}

interface NotificationBellProps {
  unreadCount: number;
  notifications: MeetingNotification[];
  isLoading: boolean;
  onOpen: () => void;
  onMarkAsRead: (meetingId: string, notificationId: string) => void;
  onDismiss: (meetingId: string, notificationId: string) => void;
  onMarkAllAsRead: () => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  unreadCount,
  notifications,
  isLoading,
  onOpen,
  onMarkAsRead,
  onDismiss,
  onMarkAllAsRead
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({ top: 0, right: 16 });

  const handleToggle = useCallback(() => {
    const next = !isOpen;
    if (next && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setIsOpen(next);
    if (next) onOpen();
  }, [isOpen, onOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Reposition panel on window resize
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      }
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [isOpen]);

  const unread = notifications.filter((n) => !n.isRead);
  const read = notifications.filter((n) => n.isRead);

  const groupByMeeting = (items: MeetingNotification[]) => {
    const groups = new Map<string, MeetingNotification[]>();
    for (const n of items) {
      const key = n.meetingId;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(n);
    }
    return groups;
  };

  const renderItem = (n: MeetingNotification) => {
    const isUnread = !n.isRead;
    const accentColor = TYPE_COLORS[n.type] || '#5f6b7a';
    const statusType = TYPE_STATUS[n.type] || 'info';
    const typeLabel = TYPE_LABELS[n.type] || n.type;

    return (
      <div
        key={n.id}
        className={`notif-item${isUnread ? ' notif-item--unread' : ''}`}
        style={{ borderLeft: `4px solid ${accentColor}` }}
        onClick={() => {
          if (isUnread) onMarkAsRead(n.meetingId, n.id);
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <StatusIndicator type={statusType}>
              <span className="notif-item__type">{typeLabel}</span>
            </StatusIndicator>
            <div className={`notif-item__message${isUnread ? ' notif-item__message--unread' : ''}`}>{formatMessage(n)}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', marginLeft: '8px', flexShrink: 0 }}>
            <span className="notif-item__time">{timeAgo(n.createdAt)}</span>
            <button
              className="notif-item__dismiss"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(n.meetingId, n.id);
              }}
              aria-label="Dismiss"
            >
              {'\u00d7'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupedSection = (label: string, items: MeetingNotification[]) => {
    if (items.length === 0) return null;
    const grouped = groupByMeeting(items);
    return (
      <>
        <div className="notif-section-label">{label}</div>
        {[...grouped.entries()].map(([key, notifs]) => (
          <div key={key}>
            <a className="notif-meeting-label" href={buildMeetingUrl(notifs[0])} target="_blank" rel="noopener noreferrer">
              📅 {notifs[0].meetingSubject}
            </a>
            {notifs.map(renderItem)}
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="notif-bell"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon name="notification" variant={unreadCount > 0 ? 'warning' : 'subtle'} />
        {unreadCount > 0 && <span className="notif-bell__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {isOpen &&
        createPortal(
          <div ref={panelRef} className="notif-panel" style={{ top: `${panelPos.top}px`, right: `${panelPos.right}px` }}>
            {/* Header */}
            <div className="notif-panel__header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="notif-panel__title">Notifications</span>
                {unreadCount > 0 && <span className="notif-panel__count-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {unreadCount > 0 && (
                  <button className="notif-panel__mark-all" onClick={onMarkAllAsRead}>
                    Mark all read
                  </button>
                )}
                <button className="notif-panel__close" onClick={() => setIsOpen(false)} aria-label="Close notifications">
                  {'\u00d7'}
                </button>
              </div>
            </div>

            {/* List */}
            <div className="notif-panel__list">
              {isLoading ? (
                <div className="notif-panel__loading">
                  <Spinner size="large" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="notif-panel__empty">
                  <Icon name="notification" size="big" variant="subtle" />
                  <div className="notif-panel__empty-title">No notifications</div>
                  <div className="notif-panel__empty-sub">You're all caught up</div>
                </div>
              ) : (
                <>
                  {renderGroupedSection('New', unread)}
                  {renderGroupedSection('Earlier', read)}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="notif-panel__footer">Showing up to 20 recent notifications</div>
          </div>,
          document.body
        )}
    </>
  );
};
